import { Typography } from '@/components/atoms';
import { MapPinnedIcon } from '@/components/atoms/icons';
import { ImageCarousel } from '@/components/molecules/ImageCarousel';
import { TextHeader } from '@/components/molecules/header/TextHeader';
import { useTranslation, useUIColors } from '@/core/hooks';
import { useAppStore } from '@/core/stores/appStore';
import { formatAdhaUnitTitle } from '@/core/utils';
import { useLocalSearchParams } from 'expo-router';
import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { PropertyDetailsAdhaStickyFooterSkeleton } from './components/PropertyDetailsAdhaStickyFooter';
import { PropertyDetailsBelowFoldShimmer } from './components/skeleton/PropertyDetailsBelowFoldShimmer';
import {
  getAdhaHeroImages,
  resolveAdhaDetailsProperty,
} from './adhaDetailsProperty';

/**
 * Instant first frame for ADHA unit details. Kept small so the card → details
 * transition can start without waiting for the full details module.
 * Reserve / Book depend on live profile, booking and appointment data, so the
 * footer is a shimmer here; the real screen keeps it until that data has loaded.
 */
export function PropertyDetailsAdhaShell() {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { propertyId } = useLocalSearchParams<{ propertyId?: string }>();
  const properties = useAppStore((state) => state.properties);
  const availableUnits = useAppStore((state) => state.availableUnits);
  const adhaProjectDetails = useAppStore((state) => state.adhaProjectDetails);
  const pendingUnitReservation = useAppStore(
    (state) => state.pendingUnitReservation,
  );
  const property = useMemo(
    () =>
      resolveAdhaDetailsProperty({
        propertyId,
        properties,
        availableUnits,
        adhaProjectDetails,
        pendingUnitReservation,
        sessionSnapshot: null,
      }),
    [propertyId, properties, availableUnits, adhaProjectDetails, pendingUnitReservation],
  );

  const projectId =
    adhaProjectDetails?.project_id ||
    useAppStore.getState().adhaLoanDetails?.projectId;
  const displayImages = useMemo(
    () =>
      getAdhaHeroImages(
        propertyId,
        projectId,
        (property as { listingImages?: unknown } | undefined)?.listingImages,
      ),
    [propertyId, projectId, property],
  );

  const formattedTitle = useMemo(() => {
    if (!property) {
      return '';
    }
    return formatAdhaUnitTitle(
      property.unitNumber,
      property.buildingName || property.projectName,
      property.projectLocation || adhaProjectDetails?.project_location || '',
    );
  }, [property, adhaProjectDetails?.project_location]);

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
      <TextHeader title={t('propertyDetails.unitDetails')} />
      <View style={styles.content}>
        <ImageCarousel
          images={displayImages}
          height={230}
          showNavigationButtons
          activeDotColor={colors.white}
          imageTransition={0}
        />
        {formattedTitle ? (
          <View style={styles.titleRow}>
            <Typography
              variant="title2"
              color="primary"
              numberOfLines={2}
              ellipsizeMode="tail"
              style={styles.titleText}
            >
              {formattedTitle}
            </Typography>
            <View style={styles.mapIconWrap}>
              <MapPinnedIcon size={24} color={colors.fontPrimary} />
            </View>
          </View>
        ) : null}
        <PropertyDetailsBelowFoldShimmer />
      </View>
      <PropertyDetailsAdhaStickyFooterSkeleton />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 120,
    gap: 16,
  },
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  titleText: {
    flex: 1,
    fontWeight: '700',
    fontSize: 22,
    lineHeight: 28,
  },
  mapIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default PropertyDetailsAdhaShell;
